import { type SearchCollectionsQuery, useSearchCollectionsInfiniteQuery } from '@vizit/api';
import { useInfiniteScroll } from '@vizit/hooks';
import * as React from 'react';
import { useCollectionGrid } from '../../providers';

interface UseCollectionGridViewerProps {
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
}

/**
 * Hook to fetch portfolio collections for the collection grid viewer with infinite loading
 * Uses the generated useSearchCollectionsInfiniteQuery query internally
 */
function useCollectionGridViewer(_props: UseCollectionGridViewerProps) {
  const { portfolioId, limit } = useCollectionGrid();

  const { data, isLoading, error, hasNextPage, hasPreviousPage, isFetchingNextPage, fetchNextPage } =
    useSearchCollectionsInfiniteQuery(
      {
        portfolio_id: portfolioId ?? '',
        sort_by: 'created_at',
        limit,
        sort_direction: 'desc',
      },
      {
        enabled: !!portfolioId,
      },
    );

  const collections = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchCollectionsQuery[] } | undefined;
    const allCollections = infiniteData?.pages?.flatMap((page) => page.data ?? []).filter((item) => item != null) || [];
    return allCollections;
  }, [data]);

  const totalCount = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchCollectionsQuery[] } | undefined;
    return infiniteData?.pages?.[0]?.total_count ?? 0;
  }, [data]);

  const sentinelRef = useInfiniteScroll({ hasNextPage: hasNextPage ?? false, isFetchingNextPage, fetchNextPage });

  // Use hasPreviousPage to determine loading count
  // hasPreviousPage = false: initial load
  // hasPreviousPage = true: subsequent loads
  const loadingCount = React.useMemo(() => {
    if (isLoading && !hasPreviousPage) {
      // Initial load: show standard limit skeletons
      return limit;
    }
    if (isFetchingNextPage) {
      // Subsequent loads: show standard limit skeletons
      return limit;
    }
    // Not loading: no skeletons needed
    return 0;
  }, [isLoading, hasPreviousPage, isFetchingNextPage, limit]);

  return {
    collections,
    totalCount,
    isLoading,
    hasError: !!error,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
    loadingCount,
  };
}

export { useCollectionGridViewer };
export type { UseCollectionGridViewerProps };
